import type { ResolvedBetterstartConfig } from '@cli-types/core-engine/config.js'
import type { GeneratedFile, GeneratorOptions, Schema } from '@cli-types/core-engine/schema.js'
import type { PipelineResult } from '@cli-types/next-adapter/generators.js'
import { normalizeEntitySchema } from '@core-engine/schema/schema-reader/normalize-entity-schema.js'
import { resolveAdminNamespace } from '@next-adapter/config/namespace/resolve-admin-namespace.js'
import { resolveExtensionNamespace } from '@next-adapter/config/paths/resolve-extension-namespace.js'
import { resolveProjectPaths } from '@next-adapter/config/paths/resolve-project-paths.js'
import { generateActions } from '@next-adapter/generators/actions/entity-actions/generate-actions.js'
import { generateColumns } from '@next-adapter/generators/columns/generate-columns.js'
import { generateCreatePage } from '@next-adapter/generators/create-page.js'
import { generateDatabase } from '@next-adapter/generators/database/generate-database.js'
import { generateEditPage } from '@next-adapter/generators/edit-page.js'
import { generateForm } from '@next-adapter/generators/form/form-entity/generate-form.js'
import { generateHook } from '@next-adapter/generators/hook/generate-hook.js'
import { namespaceGeneratedFiles } from '@next-adapter/generators/namespace-output/namespace-generated-files.js'
import { updateNavigation } from '@next-adapter/generators/navigation.js'
import { generatePageContent } from '@next-adapter/generators/page-content/generate-page-content.js'
import { generatePage } from '@next-adapter/generators/page.js'
import { generateTable } from '@next-adapter/generators/table.js'
import { updateWebhookEvents } from '@next-adapter/generators/webhook-events/update-webhook-events.js'
import { loadRelationshipSchemas } from './load-relationship-schemas.js'

export function runEntityPipeline(
  schema: Schema,
  cwd: string,
  config: ResolvedBetterstartConfig,
  options: GeneratorOptions = {}
): PipelineResult {
  const normalizedSchema = normalizeEntitySchema(schema)
  const paths = resolveProjectPaths(config)
  const outputNamespace = resolveExtensionNamespace(normalizedSchema.name, options.outputNamespace)
  const adminNamespace = resolveAdminNamespace(config.frameworkConfig.next.namespace)
  const namespacedOptions = {
    ...options,
    outputNamespace,
    adminDir: paths.adminDir,
    adminRoutePath: adminNamespace.routePath,
    adminImportAlias: adminNamespace.alias
  }
  const files: GeneratedFile[] = []
  const errors: string[] = []
  const relatedSchemas = loadRelationshipSchemas(normalizedSchema, cwd, paths.schemasDir)

  const steps: { name: string; run: () => GeneratedFile[] }[] = [
    {
      name: 'Database schema',
      run: () =>
        generateDatabase(normalizedSchema, paths.adminDbDir, namespacedOptions, relatedSchemas)
          .files
    },
    {
      name: 'Server actions',
      run: () =>
        generateActions(normalizedSchema, paths.adminActionsDir, {
          force: options.force,
          nextMajorVersion: config.nextMajorVersion,
          outputNamespace
        }).files
    },
    {
      name: 'React Query hook',
      run: () => generateHook(normalizedSchema, paths.adminHooksDir, namespacedOptions).files
    },
    {
      name: 'Column definitions',
      run: () => generateColumns(normalizedSchema, paths.pagesDir, namespacedOptions).files
    },
    {
      name: 'Table component',
      run: () => generateTable(normalizedSchema, paths.pagesDir, namespacedOptions).files
    },
    {
      name: 'Page content',
      run: () => generatePageContent(normalizedSchema, paths.pagesDir, namespacedOptions).files
    },
    {
      name: 'Page (server)',
      run: () => generatePage(normalizedSchema, paths.pagesDir, namespacedOptions).files
    }
  ]

  if (normalizedSchema.actions?.create || normalizedSchema.actions?.edit) {
    steps.push({
      name: 'Form',
      run: () => generateForm(normalizedSchema, paths.pagesDir, namespacedOptions).files
    })
  }

  if (normalizedSchema.actions?.create) {
    steps.push({
      name: 'Create page',
      run: () => generateCreatePage(normalizedSchema, paths.pagesDir, namespacedOptions).files
    })
  }

  if (normalizedSchema.actions?.edit) {
    steps.push({
      name: 'Edit page',
      run: () => generateEditPage(normalizedSchema, paths.pagesDir, namespacedOptions).files
    })
  }

  steps.push({
    name: 'Navigation',
    run: () => updateNavigation(normalizedSchema, paths.adminNavigationDir, namespacedOptions).files
  })

  steps.push({
    name: 'Webhook events',
    run: () => updateWebhookEvents(normalizedSchema, paths.adminWebhookEventsDir, 'entity').files
  })

  for (let i = 0; i < steps.length; i++) {
    const step = steps[i]
    const stepNum = i + 1
    try {
      const result = step.run()
      files.push(...result)
      if (!options.silent) console.log(`   ${stepNum}. ${step.name} ✓`)
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      errors.push(`${step.name}: ${msg}`)
      if (!options.silent) console.error(`   ${stepNum}. ${step.name} ✗ — ${msg}`)
    }
  }

  return {
    success: errors.length === 0,
    files: namespaceGeneratedFiles(files, config.frameworkConfig.next.namespace),
    errors
  }
}
